import { useTranslation } from "react-i18next";

import type { FirmwareVersion } from "~/api/firmware/types";
import { useToastStore } from "~/components/Shared/Toast/toastStore";
import {
  useUpdateFirmwareVersion,
  useUploadFirmware,
} from "~/hooks/useFirmwares";
import { DeleteFirmwareVersionModal } from "./DeleteFirmwareVersionModal";
import { FirmwareModal } from "./FirmwareModal";
import type { FirmwareModalFormData } from "./FirmwareModal";

interface FirmwareModalsProps {
  modalOpen: "upload" | "edit" | "delete" | null;
  setModalOpen: (val: "upload" | "edit" | "delete" | null) => void;
  selectedVersion: FirmwareVersion | null;
  firmwareId: number | undefined;
}

export function ShowedModals({
  modalOpen,
  setModalOpen,
  selectedVersion,
  firmwareId,
}: FirmwareModalsProps) {
  const { t } = useTranslation();
  const { pushToast } = useToastStore();
  const { mutate: uploadFirmware, isPending: isUploading } =
    useUploadFirmware();
  const { mutate: updateFirmware, isPending: isUpdating } =
    useUpdateFirmwareVersion();

  const handleUpload = (data: FirmwareModalFormData) => {
    if (!firmwareId || !data.file?.[0]) return;
    uploadFirmware(
      {
        firmwareId: Number(firmwareId),
        version: data.version,
        file: data.file[0],
        privacy_mode: data.privacy_mode,
        private_member_ids:
          data.privacy_mode === "PRIVATE"
            ? data.private_members.map((m) => m.id)
            : undefined,
      },
      {
        onSuccess: () => {
          setModalOpen(null);
          void pushToast({
            type: "success",
            title: t("general.success", "Success"),
            message: t(
              "firmware.upload_success",
              "Firmware version uploaded successfully",
            ),
          });
        },
      },
    );
  };

  const handleUpdate = (data: FirmwareModalFormData) => {
    if (!firmwareId) return;
    updateFirmware(
      Object.assign(
        {
          firmwareId: Number(firmwareId),
          firmwareVersionId: selectedVersion?.id ?? 0,
          privacy_mode: data.privacy_mode,
          private_member_ids:
            data.privacy_mode === "PRIVATE"
              ? data.private_members.map((m) => m.id)
              : undefined,
        },
        data.file?.[0] ? { file: data.file[0] } : {},
      ),
      {
        onSuccess: () => {
          setModalOpen(null);
          void pushToast({
            type: "success",
            title: t("general.success", "Success"),
            message: t(
              "firmware.update_success",
              "Firmware version updated successfully",
            ),
          });
        },
      },
    );
  };

  return (
    <>
      <FirmwareModal
        isOpen={modalOpen === "upload"}
        onClose={() => setModalOpen(null)}
        mode="upload"
        onSubmit={handleUpload}
        isPending={isUploading}
      />
      <FirmwareModal
        isOpen={modalOpen === "edit"}
        onClose={() => setModalOpen(null)}
        mode="edit"
        defaultValues={
          selectedVersion
            ? {
                version: selectedVersion.version,
                privacy_mode: selectedVersion.privacy_mode as
                  | "PRIVATE"
                  | "PUBLIC",
                private_members: selectedVersion.private_members.map((m) => ({
                  id: m.id,
                  name: m.name,
                  surname: m.surname,
                })),
              }
            : undefined
        }
        onSubmit={handleUpdate}
        isPending={isUpdating}
      />
      <DeleteFirmwareVersionModal
        isOpen={modalOpen === "delete"}
        onClose={() => setModalOpen(null)}
        version={selectedVersion ?? undefined}
        firmwareId={firmwareId ?? 0}
      />
    </>
  );
}
